import {
  AnalyticsEvent,
  AnalyticsEventNameType,
  AnalyticsUserType,
  DataSourceType,
  DefaultApi,
  GSheetOAuthTokenStatus,
  OauthAttemptStatus,
} from "@coeff/api";
import { WARNING_MESSAGES, WARNING_TITLES } from "@coeff/utils";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";

import { Button } from "../../../common/Button";
import { ConfirmBox } from "../../../common/ConfirmBox";
import { FlexRow } from "../../../common/FlexLayout";
import { WarningMessage } from "../../../common/WarningMessage";

import { buildGSheetOAuthAuthorizationUrl } from "./utils";

export type GSheetOAuthCopyContext = "import" | "alert" | "writeback" | "webapp";

const REDIRECT_POPUP_COPY: Record<
  GSheetOAuthCopyContext,
  { newUser: React.ReactNode; migration: React.ReactNode }
> = {
  import: {
    newUser: <div>To enable auto-refresh, Google needs a one-time permission.</div>,
    migration: (
      <div>
        Google now requires a <br />
        one-time permission to keep running scheduled imports. You'll only need to do this once.
      </div>
    ),
  },
  alert: {
    newUser: <div>To enable scheduled alerts, Google needs a one-time permission.</div>,
    migration: (
      <div>
        Google now requires a <br />
        one-time permission to keep running your alerts. You'll only need to do this once.
      </div>
    ),
  },
  writeback: {
    newUser: <div>To enable scheduled writebacks, Google needs a one-time permission.</div>,
    migration: (
      <div>
        Google now requires a <br />
        one-time permission to keep running your scheduled writebacks. You'll only need to do this
        once.
      </div>
    ),
  },
  webapp: {
    newUser: (
      <div>To continue, Google needs a one-time permission to access your Google Sheets.</div>
    ),
    migration: (
      <div>
        Google now requires a <br />
        one-time permission to access your Google Sheets. You'll only need to do this once.
      </div>
    ),
  },
};

export type AuthorizeGSheetContentProps = {
  /** Which surface/flow this is rendered from — drives the copy shown. */
  copyContext: GSheetOAuthCopyContext;
  oauthTokenStatus: GSheetOAuthTokenStatus;
  email: string;
  clientId: string;
  redirectUri: string;
  api: Pick<DefaultApi, "createOauthAttempt" | "getOauthAttempt">;
  track: (eventName: AnalyticsEventNameType, extraProperties?: AnalyticsEvent) => void;
  onSuccess: () => void;
  onCancel: () => void;
  /** Excel desktop can't reliably detect popup blocking; addon passes true for that case. */
  bypassPopupBlockedCheck?: boolean;
  sentryCapture?: (args: { error: unknown; name: string }) => void;
};

export const AuthorizeGSheetContent: React.FC<AuthorizeGSheetContentProps> = ({
  copyContext,
  oauthTokenStatus,
  email,
  clientId,
  redirectUri,
  api,
  track,
  onSuccess,
  onCancel,
  bypassPopupBlockedCheck = false,
  sentryCapture,
}) => {
  const [oauthAttemptId, setOauthAttemptId] = useState<string>();

  const [oauthInProgress, setOauthInProgress] = useState(false);

  const [popupBlocked, setPopupBlocked] = useState<boolean>(false);

  const userType =
    oauthTokenStatus === GSheetOAuthTokenStatus.MigrationRequired
      ? AnalyticsUserType.ScheduledImports
      : AnalyticsUserType.NoScheduledImports;

  useEffect(() => {
    track(AnalyticsEventNameType.GsheetsRedirectNoticePopupShown, { user_type: userType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: oauthAttemptResponse } = useQuery(
    ["GSHEET_OAUTH_ATTEMPT", oauthAttemptId],
    () => api.getOauthAttempt({ oauth_attempt_id: oauthAttemptId! }),
    {
      enabled: Boolean(oauthInProgress && oauthAttemptId),
      refetchInterval: response =>
        response?.data.oauth_attempt.status !== OauthAttemptStatus.InProgress ? false : 2_000,
    }
  );

  useEffect(() => {
    switch (oauthAttemptResponse?.data?.oauth_attempt.status) {
      case OauthAttemptStatus.Success:
        track(AnalyticsEventNameType.GsheetsAddonAuthTokenAcquired, { user_type: userType });
        setOauthInProgress(false);
        onSuccess();
        break;
      case OauthAttemptStatus.Failure:
        track(AnalyticsEventNameType.GsheetsAddonAuthTokenAcquisitionRejected, {
          user_type: userType,
        });
        setOauthInProgress(false);
        onCancel();
        break;
      case OauthAttemptStatus.InProgress:
        // Ignore if in progress
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthAttemptResponse]);

  const handleCancel = () => {
    track(AnalyticsEventNameType.GsheetsRedirectNoticePopupClosedByUser, { user_type: userType });
    setOauthInProgress(false);
    onCancel();
  };

  const fetchRefreshTokenAndSave = async () => {
    track(AnalyticsEventNameType.GsheetsRedirectNoticePopupOkByUser, { user_type: userType });
    try {
      const { data: response } = await api.createOauthAttempt();
      const attemptId = response?.oauth_attempt?.oauth_attempt_id ?? "";

      const authorizationUrl = buildGSheetOAuthAuthorizationUrl({
        clientId,
        redirectUri,
        email,
        oauthAttemptId: attemptId,
      });

      const newTab = window.open(authorizationUrl, "_blank");

      if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
        track(AnalyticsEventNameType.AuthorizationWindowPopupBlocked, {
          data_source: DataSourceType.GoogleSheets,
          data_source_id: undefined,
        });
        if (!bypassPopupBlockedCheck) {
          setPopupBlocked(true);
          setOauthInProgress(false);
          return;
        }
      }

      newTab?.focus();

      setOauthInProgress(true);
      setPopupBlocked(false);
      setOauthAttemptId(attemptId);
    } catch (error: unknown) {
      sentryCapture?.({
        error,
        name: "AuthorizeGSheetContent: POST /api/auth/gsheets_auth_callback",
      });
    }
  };

  return (
    <ConfirmBox
      title={
        oauthInProgress
          ? "Waiting for authorization"
          : oauthTokenStatus === GSheetOAuthTokenStatus.MigrationRequired
          ? "Action Needed"
          : "Authorization Required"
      }
      visible={true}
      showOk={false}
      closable={true}
      onCancel={handleCancel}
      showCancel={false}
    >
      {popupBlocked && (
        <WarningMessage
          title={WARNING_TITLES.POPUP_BLOCKED}
          message={WARNING_MESSAGES.POPUP_BLOCKED}
          style={{ marginBottom: 16 }}
        />
      )}

      {oauthInProgress ? (
        <div>Complete authorization in the other tab, then return to this page.</div>
      ) : oauthTokenStatus === GSheetOAuthTokenStatus.MigrationRequired ? (
        REDIRECT_POPUP_COPY[copyContext].migration
      ) : (
        REDIRECT_POPUP_COPY[copyContext].newUser
      )}

      {!oauthInProgress && (
        <FlexRow style={{ marginTop: 24 }}>
          <Button type="primary" style={{ width: "100%" }} onClick={fetchRefreshTokenAndSave}>
            Continue
          </Button>
        </FlexRow>
      )}
    </ConfirmBox>
  );
};
